import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Home, RefreshCw } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leaderboard = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  return (
    <div 
      className="min-h-screen p-8"
      style={{ background: 'linear-gradient(180deg, #FDFBF7 0%, #FFFBEB 100%)' }}
      data-testid="leaderboard-screen"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/1cd2d0b9-b28c-4677-afeb-8f22de75f592/images/cb6467a5d353412edb2ba0c04e00d82abc2cfe45d2e687b3beaf9e0a5d670c9d.png"
            alt="Trophy"
            className="w-32 h-32 mx-auto mb-6"
            data-testid="trophy-image"
          />
          <h1 
            className="text-5xl sm:text-6xl tracking-tighter uppercase mb-4 text-[#121212]"
            style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 900 }}
            data-testid="leaderboard-title"
          >
            LEADERBOARD
          </h1>
          <p className="text-lg font-bold text-[#333333]">Today's Fastest Times</p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8 justify-center flex-wrap">
          <button
            onClick={() => navigate('/')}
            className="bg-[#FFD500] border-4 border-[#121212] px-6 py-3 text-lg font-black uppercase neo-shadow neo-button transition-all"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="home-button"
          >
            <Home className="inline mr-2" strokeWidth={3} />
            Home
          </button>
          <button
            onClick={fetchLeaderboard}
            className="bg-[#0055FF] text-white border-4 border-[#121212] px-6 py-3 text-lg font-black uppercase neo-shadow neo-button transition-all"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="refresh-button"
          >
            <RefreshCw className="inline mr-2" strokeWidth={3} />
            Refresh
          </button>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-white border-4 border-[#121212] neo-shadow overflow-hidden" data-testid="leaderboard-table">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#121212] border-t-[#FFD500]"></div>
              <p className="mt-4 font-bold text-[#333333]">Loading...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-entries">
              <Trophy className="mx-auto mb-4 text-[#121212]/30" size={48} strokeWidth={3} />
              <p className="text-lg font-bold text-[#333333]">No entries yet today!</p>
              <p className="text-sm font-bold text-[#333333] mt-2">Be the first to complete the race!</p>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div className="bg-[#FFD500] border-b-4 border-[#121212] px-6 py-4 grid grid-cols-12 gap-4 font-black uppercase text-sm tracking-[0.15em]">
                <div className="col-span-2">Rank</div>
                <div className="col-span-6">Player</div>
                <div className="col-span-4 text-right">Time</div>
              </div>

              {/* Entries */}
              {entries.map((entry, index) => {
                const isTopThree = index < 3;
                const rankColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                const medalEmojis = ['🥇', '🥈', '🥉'];

                return (
                  <div
                    key={entry.id}
                    className={`px-6 py-4 grid grid-cols-12 gap-4 items-center border-b-4 border-[#121212] ${
                      isTopThree ? 'bg-[#FFFBEB]' : 'bg-white'
                    }`}
                    data-testid={`leaderboard-entry-${index}`}
                  >
                    <div className="col-span-2">
                      {isTopThree ? (
                        <span className="text-3xl">{medalEmojis[index]}</span>
                      ) : (
                        <span className="text-2xl font-black text-[#121212]">#{index + 1}</span>
                      )}
                    </div>
                    <div className="col-span-6">
                      <p className="text-lg font-black text-[#121212] truncate">{entry.player_name}</p>
                    </div>
                    <div className="col-span-4 text-right">
                      <p className="text-xl font-black text-[#0055FF]">{entry.completion_time.toFixed(1)}s</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats */}
        {entries.length > 0 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="leaderboard-stats">
            <div className="bg-white border-4 border-[#121212] p-4 neo-shadow text-center">
              <p className="text-sm uppercase tracking-[0.15em] mb-2 font-bold text-[#333333]">Fastest Time</p>
              <p className="text-3xl font-black text-[#00E676]">{entries[0].completion_time.toFixed(1)}s</p>
            </div>
            <div className="bg-white border-4 border-[#121212] p-4 neo-shadow text-center">
              <p className="text-sm uppercase tracking-[0.15em] mb-2 font-bold text-[#333333]">Average Time</p>
              <p className="text-3xl font-black text-[#0055FF]">
                {(entries.reduce((sum, e) => sum + e.completion_time, 0) / entries.length).toFixed(1)}s
              </p>
            </div>
            <div className="bg-white border-4 border-[#121212] p-4 neo-shadow text-center">
              <p className="text-sm uppercase tracking-[0.15em] mb-2 font-bold text-[#333333]">Total Racers</p>
              <p className="text-3xl font-black text-[#FF9500]">{entries.length}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
